/* eslint-disable no-unused-vars */
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Calendar,
  Video,
  Headphones,
  Users,
  BookOpen,
  Heart,
  DollarSign,
  Sparkles,
  MessageCircle,
  Mic,
  ExternalLink,
  Brain,
  Dumbbell,
  Compass,
  Landmark,
  ArrowRight,
  Check,
  Flame,
  CalendarCheck,
  Quote,
} from 'lucide-react';
import PortalSubPage, { ComingSoonCard } from '../PortalSubPage';
import {
  NEXT_MEETING,
  INTEREST_MAILTO,
  INTEREST_EMAIL,
  CIRCLE_FOCUS,
  CIRCLE_GUEST_SPEAKER,
  CIRCLE_TOPICS,
  CIRCLE_PRICE,
  getReflectionForOffset,
} from '../../data/portalData';

export function PortalReset2027() {
  const target = new Date('2027-08-12T00:00:00');
  const now = new Date();
  const totalMs = target - now;
  const days = Math.max(0, Math.ceil(totalMs / (1000 * 60 * 60 * 24)));
  const weeks = Math.max(0, Math.floor(days / 7));
  const months = Math.max(
    0,
    (target.getFullYear() - now.getFullYear()) * 12 +
      (target.getMonth() - now.getMonth()),
  );

  return (
    <PortalSubPage
      eyebrow="The Reset · 2027"
      title={
        <>
          Panama, <span className="italic text-[#D4AF37]">again.</span>
        </>
      }
      intro="August 12–14, 2027 · The Westin Playa Bonita, Panama City. The brotherhood returns."
      icon={Sparkles}
    >
      {/* Countdown hero */}
      <div
        className="rounded-2xl bg-gradient-to-br from-[#D4AF37]/20 via-[#8C6239]/10 to-transparent border border-[#D4AF37]/30 p-8 md:p-10"
        data-testid="reset-2027-countdown"
      >
        <p className="text-[#D4AF37] font-manrope text-[11px] uppercase tracking-[0.3em] font-bold mb-4">
          Countdown to Panama
        </p>
        <div className="flex flex-wrap items-end gap-6 md:gap-10">
          <div>
            <p className="font-cormorant text-7xl md:text-8xl font-medium text-white leading-none">
              {days}
            </p>
            <p className="text-zinc-400 font-manrope text-sm mt-2">days to go</p>
          </div>
          <div className="border-l border-white/10 pl-6 md:pl-10 flex gap-6 md:gap-10">
            <div>
              <p className="font-cormorant text-3xl md:text-4xl font-medium text-white">
                {weeks}
              </p>
              <p className="text-zinc-500 font-manrope text-xs">weeks</p>
            </div>
            <div>
              <p className="font-cormorant text-3xl md:text-4xl font-medium text-white">
                {months}
              </p>
              <p className="text-zinc-500 font-manrope text-xs">months</p>
            </div>
          </div>
        </div>
      </div>

      {/* Details grid */}
      <div className="grid md:grid-cols-3 gap-4">
        <div className="rounded-2xl bg-[#111111] border border-white/10 p-6">
          <p className="text-[#D4AF37] font-manrope text-[11px] uppercase tracking-widest font-bold mb-2">
            When
          </p>
          <p className="text-white font-cormorant text-2xl font-medium leading-tight">
            Aug 12&ndash;14
          </p>
          <p className="text-zinc-500 font-manrope text-sm mt-1">2027</p>
        </div>
        <div className="rounded-2xl bg-[#111111] border border-white/10 p-6">
          <p className="text-[#D4AF37] font-manrope text-[11px] uppercase tracking-widest font-bold mb-2">
            Where
          </p>
          <p className="text-white font-cormorant text-2xl font-medium leading-tight">
            Westin Playa Bonita
          </p>
          <p className="text-zinc-500 font-manrope text-sm mt-1">Panama City, Panama</p>
        </div>
        <div className="rounded-2xl bg-[#111111] border border-white/10 p-6">
          <p className="text-[#D4AF37] font-manrope text-[11px] uppercase tracking-widest font-bold mb-2">
            Who
          </p>
          <p className="text-white font-cormorant text-2xl font-medium leading-tight">
            Black men
          </p>
          <p className="text-zinc-500 font-manrope text-sm mt-1">
            ready to reset
          </p>
        </div>
      </div>

      {/* Waitlist */}
      <Reset2027Waitlist />
    </PortalSubPage>
  );
}

function Reset2027Waitlist() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState(null); // {type: 'success'|'error'|'already', message: string}

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) {
      setStatus({ type: 'error', message: 'Please enter your name and email.' });
      return;
    }
    setSubmitting(true);
    setStatus(null);
    try {
      const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
      const res = await fetch(`${API}/reset-2027/waitlist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: name.trim(),
          email: email.trim(),
          source: 'portal_reset_2027',
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Something went wrong.');
      }
      const data = await res.json();
      setStatus({
        type: data.already_on_list ? 'already' : 'success',
        message: data.message,
      });
      if (!data.already_on_list) {
        setName('');
        setEmail('');
      }
    } catch (err) {
      setStatus({ type: 'error', message: err.message || 'Could not join the list. Try again.' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      id="reset-2027-waitlist"
      className="rounded-2xl bg-[#111111] border border-[#D4AF37]/25 p-8 md:p-10 scroll-mt-24"
      data-testid="reset-2027-waitlist"
    >
      <p className="text-[#D4AF37] font-manrope text-[11px] uppercase tracking-[0.3em] font-bold mb-3">
        Reserve Your Spot
      </p>
      <h3 className="font-cormorant text-3xl md:text-4xl font-medium text-white mb-3">
        Join the 2027 <span className="italic text-[#D4AF37]">early-bird list.</span>
      </h3>
      <p className="text-zinc-400 font-manrope max-w-lg mb-6 leading-relaxed">
        Early-bird pricing, payment plans, and room selection drop first to the waitlist.
        Add your name and we&rsquo;ll email you the moment registration opens.
      </p>

      {status?.type === 'success' || status?.type === 'already' ? (
        <div
          className="rounded-xl bg-[#D4AF37]/10 border border-[#D4AF37]/40 p-5 flex items-start gap-3"
          data-testid="reset-2027-waitlist-success"
        >
          <div className="w-8 h-8 rounded-full bg-[#D4AF37]/20 border border-[#D4AF37]/40 flex items-center justify-center flex-shrink-0">
            <span className="text-[#D4AF37] text-lg leading-none">&#10003;</span>
          </div>
          <div>
            <p className="text-white font-cormorant text-xl font-medium">
              {status.type === 'already' ? 'You\u2019re already in.' : 'Welcome to the waitlist.'}
            </p>
            <p className="text-zinc-400 font-manrope text-sm mt-1">{status.message}</p>
            <a
              href={INTEREST_MAILTO}
              className="inline-flex items-center gap-2 text-[#D4AF37] font-manrope text-sm mt-3 hover:text-amber-200"
            >
              Have a question? Reach out
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
          </div>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="text-zinc-400 font-manrope text-xs uppercase tracking-widest block mb-2">
                Your Name
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name"
                className="w-full bg-[#0A0A0A] border border-white/10 rounded-lg px-4 py-3 text-white font-manrope focus:outline-none focus:border-[#D4AF37]/60 transition-colors"
                data-testid="reset-2027-waitlist-name"
                disabled={submitting}
              />
            </div>
            <div>
              <label className="text-zinc-400 font-manrope text-xs uppercase tracking-widest block mb-2">
                Email
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@email.com"
                className="w-full bg-[#0A0A0A] border border-white/10 rounded-lg px-4 py-3 text-white font-manrope focus:outline-none focus:border-[#D4AF37]/60 transition-colors"
                data-testid="reset-2027-waitlist-email"
                disabled={submitting}
              />
            </div>
          </div>

          {status?.type === 'error' && (
            <p
              className="text-red-400 font-manrope text-sm"
              data-testid="reset-2027-waitlist-error"
            >
              {status.message}
            </p>
          )}

          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pt-2">
            <p className="text-zinc-500 font-manrope text-xs">
              No spam. Unsubscribe anytime. Emails go to {INTEREST_EMAIL}.
            </p>
            <button
              type="submit"
              disabled={submitting}
              className="bg-[#D4AF37] text-[#0A0A0A] px-8 py-3.5 rounded-full font-manrope font-semibold hover:bg-amber-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
              data-testid="reset-2027-waitlist-submit"
            >
              {submitting ? 'Adding you\u2026' : 'Join the Waitlist'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
